import { Box, Flex, NoSsr, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { CONTAINER_BLOCK_PADDING_X, CONTENT_BLOCK_MAX_WIDTH, FULL_BLEED_MARGIN_X, getBrannanMobileMediaQuery } from '../../LayoutAloha.theme';
import { MobileOnlyCoverPhotoBox, StyledTitle } from './Page.styles';
import { PrettyLink } from '@apps/guest/packages/layout-engine/components/PrettyLink';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import type { PhotoV2WithoutBlurHashFragment } from '@graphql/fragments/photo.graphql.generated';
import type { EventPageVisibility, EventPageType } from '@graphql/generated-base';
import { DesignLayoutType } from '@graphql/generated-base';
import { LazyRichText } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichText.lazy';
import { isRichTextContent } from '@shared/utils/html';
import { isRegistryPage } from '@shared/utils/migratedCustomPages';
import CoverPhoto from '../../../../components/CoverPhoto';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { AccommodationsCountdownExperiment } from '@apps/guest/components/AccommodationsCountdownExperiment';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';

// PRNT-4881: title/subtitle used to fade in behind `useShouldRevealPageContent`, the same
// signal that defers each stacked page's WIDGET data (a real per-page GraphQL query — see
// Body.tsx's <NoSsr> wrap). But `title`/`subtitle` here come from the already-loaded `page`
// list (Body.tsx passes `page.pageTitle` / `page.subTitle` straight through — Body returns
// null before `page` exists), so they're present in the DOM at first paint on every stacked
// page, not just the deep-linked target. Gating their opacity on the target's widget-load
// signal held them invisible — sometimes past REVEAL_ALL_TIMEOUT_MS on a slow connection —
// while a guest scrolling through saw blank space where every section title should be.
// Render them immediately; no chrome to fade.

interface Props
  extends Readonly<{
    title?: Maybe<string>;
    subtitle?: Maybe<string>;
    graphicAccent?: Maybe<JSX.Element>;
    marginTop?: string | string[];
    isAccommodations?: boolean;
    pageSlug?: string;
    photo?: Maybe<PhotoV2WithoutBlurHashFragment>;
    pageId: string;
    pageType: EventPageType;
    visibility: EventPageVisibility;
  }> {}

export const Page = React.forwardRef<HTMLDivElement, React.PropsWithChildren<Props>>((props, pageRef) => {
  const { title, subtitle, graphicAccent, children, isAccommodations, pageSlug, photo, pageId, pageType, visibility, ...restProps } = props;

  const isSmallScreen = useMediaQuery(getBrannanMobileMediaQuery);
  const { pageFontColor } = useColorPaletteProvider();

  return isAccommodations ? (
    <Flex
      ref={pageRef}
      {...restProps}
      paddingX={CONTAINER_BLOCK_PADDING_X}
      width="100%"
      justifyContent={'center'}
      flexDirection={'column'}
    >
      {photo && (
        <MobileOnlyCoverPhotoBox
          paddingBottom={{ _: 9, sm2: 10 }}
          marginX={FULL_BLEED_MARGIN_X}
        >
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </MobileOnlyCoverPhotoBox>
      )}
      {graphicAccent}
      {title && (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          {/* Plain TextV2 instead of ResponsiveText: the JS-based font resize in ResponsiveText
              fires 200ms after mount, shifting the title from the SSR-rendered display3 size to the
              computed fit-to-width size — a guaranteed CLS hit. Letting the typography variant CSS
              set the font size (via GuestSiteTypographyOverride) respects event font scaling and
              is consistent with every other Aloha page title (StyledTitle). Long titles wrap
              naturally via wordBreak. */}
          <TextV2
            typographyVariant="display3"
            marginTop={{ _: 0, sm2: 5 }}
            marginX={6}
            textAlign={'center'}
            wordBreak="break-word"
          >
            {title}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
      {subtitle && (
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            as="div"
            typographyVariant="body4"
            marginTop={7}
            marginX={'auto'}
            textAlign={'center'}
            maxWidth={CONTENT_BLOCK_MAX_WIDTH}
            wordBreak="break-word"
            color={pageFontColor || 'mono14'}
          >
            {/* Rich-text subtitles render via LazyRichText directly (no NoSsr swap) so SSR
                and client agree — eliminates the plain→rich CLS. Plain-text subtitles keep
                PrettyLink for email auto-linking. The regex detects a real HTML tag (e.g. `<p>`,
                `<div>`) and is SSR-safe (no DOM needed), unlike `isRichTextContent`. A naive
                `startsWith('<')` would misroute plain text like "<3 you". */}
            {/^\s*<[a-zA-Z]/.test(subtitle) ? (
              <LazyRichText
                richTextComponentData={{ id: pageId, text: subtitle }}
                sanitize
                textAlign="center"
              />
            ) : (
              <PrettyLink
                source={pageSlug}
                allowedTypes={['EMAIL']}
              >
                {subtitle}
              </PrettyLink>
            )}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
      <AccommodationsCountdownExperiment />
      {children}
    </Flex>
  ) : (
    <Box
      ref={pageRef}
      {...restProps}
      paddingX={CONTAINER_BLOCK_PADDING_X}
      marginX={'auto'}
    >
      {isSmallScreen && photo && (
        <Box
          paddingBottom={{ _: 9, sm2: 10 }}
          marginX={FULL_BLEED_MARGIN_X}
        >
          <CoverPhoto
            websiteLayoutType={DesignLayoutType.aloha}
            photo={photo}
            pageId={pageId}
            pageType={pageType}
            pageSlug={pageSlug}
          />
        </Box>
      )}
      {graphicAccent}
      {title ? (
        <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
          <StyledTitle
            typographyVariant="display3"
            marginTop={{ _: 0, sm2: 5 }}
            textAlign={'center'}
          >
            {title}
          </StyledTitle>
        </GuestSiteTypographyOverride>
      ) : null}
      {subtitle ? (
        <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
          <TextV2
            as="div"
            typographyVariant="body4"
            marginTop={7}
            marginX="auto"
            textAlign="center"
            maxWidth={CONTENT_BLOCK_MAX_WIDTH}
            wordBreak="break-word"
          >
            {(() => {
              const plainSubtitle = <PrettyLink source={pageSlug}>{subtitle}</PrettyLink>;
              if (!isRegistryPage(pageType, pageSlug)) return plainSubtitle;
              // SSR/client must agree: `isRichTextContent` is DOM-only and returns false on the server,
              // so wrap in NoSsr with the plain PrettyLink as fallback. SSR + first hydration emit the
              // fallback; the rich render swaps in after mount.
              return (
                <NoSsr fallback={plainSubtitle}>{isRichTextContent(subtitle) ? <LazyRichText richTextComponentData={{ id: pageId, text: subtitle }} /> : plainSubtitle}</NoSsr>
              );
            })()}
          </TextV2>
        </GuestSiteTypographyOverride>
      ) : null}
      {children}
    </Box>
  );
});
